/** QuickPlan — Évaluateur d'expressions paramétriques (sûr : aucun eval).
 *
 * Les dimensions des composants s'écrivent en nombres OU en expressions sur leurs
 * paramètres : "L", "L-2*E", "max(1, floor(L/500))", "(U*44.45)-0.8".
 * Grammaire : + - * / %, parenthèses, moins unaire, nombres décimaux, identifiants
 * (paramètres), fonctions min max round floor ceil abs sqrt.
 */

const FUNCS = {
  min: Math.min,
  max: Math.max,
  round: Math.round,
  floor: Math.floor,
  ceil: Math.ceil,
  abs: Math.abs,
  sqrt: Math.sqrt,
};

function tokenize(src) {
  const out = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t') { i++; continue; }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j])) j++;
      // Notation scientifique : 1e6, 2.5E-3
      const exp = src.slice(j).match(/^[eE][+-]?\d+/);
      if (exp) j += exp[0].length;
      const n = Number(src.slice(i, j));
      if (Number.isNaN(n)) throw new Error(`nombre invalide « ${src.slice(i, j)} »`);
      out.push({ t: 'num', v: n });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < src.length && /[\w]/.test(src[j])) j++;
      out.push({ t: 'id', v: src.slice(i, j) });
      i = j;
      continue;
    }
    if ('+-*/%(),'.includes(c)) { out.push({ t: c }); i++; continue; }
    throw new Error(`caractère inattendu « ${c} »`);
  }
  return out;
}

/** Évalue une expression. `vars` = { L: 500, … }. Un nombre est rendu tel quel. */
export function evalExpr(expr, vars = {}) {
  if (typeof expr === 'number') return expr;
  if (typeof expr !== 'string' || !expr.trim()) throw new Error('expression vide');
  const toks = tokenize(expr);
  let p = 0;
  const peek = () => toks[p];
  const eat = (t) => {
    if (toks[p]?.t !== t) throw new Error(`« ${t} » attendu dans « ${expr} »`);
    return toks[p++];
  };

  function parseExpr() {
    let v = parseTerm();
    while (peek() && (peek().t === '+' || peek().t === '-')) {
      const op = toks[p++].t;
      const r = parseTerm();
      v = op === '+' ? v + r : v - r;
    }
    return v;
  }
  function parseTerm() {
    let v = parseFactor();
    while (peek() && (peek().t === '*' || peek().t === '/' || peek().t === '%')) {
      const op = toks[p++].t;
      const r = parseFactor();
      if (op === '*') v *= r;
      else if (op === '/') v /= r;
      else v %= r;
    }
    return v;
  }
  function parseFactor() {
    const tok = peek();
    if (!tok) throw new Error(`expression incomplète « ${expr} »`);
    if (tok.t === '-') { p++; return -parseFactor(); }
    if (tok.t === '+') { p++; return parseFactor(); }
    if (tok.t === 'num') { p++; return tok.v; }
    if (tok.t === '(') {
      p++;
      const v = parseExpr();
      eat(')');
      return v;
    }
    if (tok.t === 'id') {
      p++;
      if (peek()?.t === '(') {
        const fn = FUNCS[tok.v];
        if (!fn) throw new Error(`fonction inconnue « ${tok.v} »`);
        p++;
        const args = [];
        if (peek()?.t !== ')') {
          args.push(parseExpr());
          while (peek()?.t === ',') { p++; args.push(parseExpr()); }
        }
        eat(')');
        return fn(...args);
      }
      if (!(tok.v in vars)) throw new Error(`paramètre inconnu « ${tok.v} »`);
      return Number(vars[tok.v]);
    }
    throw new Error(`jeton inattendu « ${tok.t} » dans « ${expr} »`);
  }

  const v = parseExpr();
  if (p < toks.length) throw new Error(`reste inattendu dans « ${expr} »`);
  if (!Number.isFinite(v)) throw new Error(`résultat non fini pour « ${expr} »`);
  return v;
}

/** Évalue un vecteur [e, e, e]. */
export function evalVec(vec, vars) {
  return (vec ?? [0, 0, 0]).map((e) => evalExpr(e, vars));
}

/** Remplit un gabarit « {W}×{H} mm » : chaque {expr} est évaluée et arrondie à 0,1. */
export function fillTemplate(tpl, vars) {
  if (!tpl) return '';
  return tpl.replace(/\{([^}]+)\}/g, (_, e) => {
    try {
      const v = evalExpr(e, vars);
      return String(Math.round(v * 10) / 10).replace('.', ',');
    } catch {
      return `{${e}}`;
    }
  });
}
